import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from './adminApi';
import { AdminOtpDialog } from './AdminOtpDialog';

/**
 * Hook that wraps any sensitive SuperAdmin save action behind the
 * email-only (Part C) or dual email+WhatsApp (Part D) OTP guard.
 *
 * Usage:
 *
 *   const { guard, dialog } = useAdminOtpGuard();
 *
 *   const saveSmtp = async () => {
 *     await guard({
 *       requestOtpUrl: '/admin/email-integrations/request-otp',
 *       title: 'Confirm SMTP save',
 *       performSave: async (headers) => {
 *         await adminApi.put('/admin/email-integrations/smtp', body, { headers });
 *         toast.success('SMTP saved');
 *         await refresh();
 *       },
 *     });
 *   };
 *
 *   return (<>
 *     ...
 *     {dialog}
 *   </>);
 *
 * The hook handles dispatching the OTP request, parking the save action,
 * collecting the 6-digit codes in a shared dialog, then forwarding the
 * required `X-Admin-Email-OTP` (and `X-Admin-WhatsApp-OTP`) headers to
 * the supplied `performSave` callback. The save action only runs after
 * the user enters all required codes and clicks Verify.
 */
export function useAdminOtpGuard() {
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [whatsappOtp, setWhatsappOtp] = useState('');
  const [requiredChannels, setRequiredChannels] = useState(['email']);
  const [whatsappFallback, setWhatsappFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState('');
  const [emailMasked, setEmailMasked] = useState('');
  const [whatsappMasked, setWhatsappMasked] = useState('');
  // Pending save callback (boxed in a ref-style holder to keep arity).
  const [pending, setPending] = useState(null);
  const [title, setTitle] = useState('Confirm with OTP');
  const [description, setDescription] = useState(
    'For security, this admin action needs OTP verification.',
  );

  const reset = useCallback(() => {
    setEmailOtp('');
    setWhatsappOtp('');
    setRequiredChannels(['email']);
    setWhatsappFallback(false);
    setFallbackReason('');
    setEmailMasked('');
    setWhatsappMasked('');
    setPending(null);
  }, []);

  const guard = useCallback(
    async ({ requestOtpUrl, performSave, title: t, description: d }) => {
      try {
        const r = await adminApi.post(requestOtpUrl);
        if (r.data?.ok === false) {
          toast.error(r.data?.error || 'Could not send OTP. Please try again.');
          return false;
        }
        setRequiredChannels(r.data?.required_channels || ['email']);
        setWhatsappFallback(!!r.data?.whatsapp_fallback);
        setFallbackReason(r.data?.fallback_reason || '');
        setEmailMasked(r.data?.email_masked || '');
        setWhatsappMasked(r.data?.whatsapp_masked || '');
        if (t) setTitle(t);
        if (d) setDescription(d);
        // Wrap the callback so React's setter doesn't invoke it.
        setPending(() => performSave);
        setOpen(true);
        toast.success('OTP sent — enter the code(s) to confirm.');
        return true;
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to send OTP';
        toast.error(typeof msg === 'string' ? msg : 'Failed to send OTP');
        return false;
      }
    },
    [],
  );

  const verifyAndSave = useCallback(async () => {
    if (!pending) return;
    const needsWa = requiredChannels.includes('whatsapp');
    if (emailOtp.length !== 6) {
      toast.error('Enter the 6-digit Email OTP.');
      return;
    }
    if (needsWa && whatsappOtp.length !== 6) {
      toast.error('Enter the 6-digit WhatsApp OTP.');
      return;
    }
    setVerifying(true);
    try {
      const headers = { 'X-Admin-Email-OTP': emailOtp };
      if (needsWa) headers['X-Admin-WhatsApp-OTP'] = whatsappOtp;
      await pending(headers);
      setOpen(false);
      reset();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(
        typeof d === 'string'
          ? d
          : 'Save failed — verify your OTP(s) and try again.',
      );
    } finally {
      setVerifying(false);
    }
  }, [pending, requiredChannels, emailOtp, whatsappOtp, reset]);

  const dialog = (
    <AdminOtpDialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
      emailOtp={emailOtp}
      setEmailOtp={setEmailOtp}
      whatsappOtp={whatsappOtp}
      setWhatsappOtp={setWhatsappOtp}
      requiredChannels={requiredChannels}
      whatsappFallback={whatsappFallback}
      fallbackReason={fallbackReason}
      emailMasked={emailMasked}
      whatsappMasked={whatsappMasked}
      verifying={verifying}
      onVerify={verifyAndSave}
      title={title}
      description={description}
    />
  );

  return { guard, dialog };
}

export default useAdminOtpGuard;
